'use client';

/**
 * OperationsTile - Displays operations content (policies, procedures, training)
 *
 * Shows operations information including:
 * - Policy/procedure name
 * - Document type (law, policy, procedure, training)
 * - Effective dates
 * - Compliance indicators
 */

import {
  Scale,
  BookOpen,
  ClipboardList,
  GraduationCap,
  Shield,
  Calendar,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { SearchResult } from '../types';

/**
 * Extended operations result with specific metadata
 */
interface OperationsResult extends SearchResult {
  type: 'operations';
  metadata: {
    /** Type of operations document */
    documentType?: 'law' | 'policy' | 'procedure' | 'training' | 'incident';
    /** Effective date */
    effectiveDate?: string;
    /** Expiration date */
    expirationDate?: string;
    /** Compliance status */
    complianceStatus?: 'compliant' | 'review_needed' | 'expired';
    /** Related regulations */
    regulations?: string[];
    /** Department */
    department?: string;
    /** Last reviewed date */
    lastReviewed?: string;
  };
}

/**
 * Get icon based on document type
 */
function getDocumentIcon(documentType?: string) {
  switch (documentType) {
    case 'law':
      return Scale;
    case 'policy':
      return Shield;
    case 'procedure':
      return ClipboardList;
    case 'training':
      return GraduationCap;
    default:
      return BookOpen;
  }
}

/**
 * Get icon color based on document type
 */
function getIconColor(documentType?: string): string {
  switch (documentType) {
    case 'law':
      return 'text-rose-400';
    case 'policy':
      return 'text-cyan-400';
    case 'procedure':
      return 'text-emerald-400';
    case 'training':
      return 'text-purple-400';
    default:
      return 'text-cyan-400';
  }
}

/**
 * Compliance status badge
 */
function ComplianceBadge({ status }: { status?: OperationsResult['metadata']['complianceStatus'] }) {
  if (!status) return null;

  const config = {
    compliant: {
      icon: CheckCircle,
      label: 'Compliant',
      className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    },
    review_needed: {
      icon: AlertCircle,
      label: 'Review Needed',
      className: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    },
    expired: {
      icon: AlertCircle,
      label: 'Expired',
      className: 'bg-red-500/10 text-red-400 border-red-500/30',
    },
  };

  const cfg = config[status];
  if (!cfg) return null;

  const Icon = cfg.icon;

  return (
    <Badge
      variant="outline"
      className={cn('text-xs gap-1', cfg.className)}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

/**
 * Document type badge
 */
function DocumentTypeBadge({ type }: { type?: string }) {
  if (!type) return null;

  return (
    <Badge
      variant="outline"
      className="text-xs bg-slate-800/50 border-slate-700/50 text-slate-400 capitalize"
    >
      {type}
    </Badge>
  );
}

/**
 * Format date for display
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * OperationsTile component
 */
export const OperationsTile = createTile<OperationsResult>(
  'OperationsTile',
  (result) => {
    const { metadata } = result;
    const Icon = getDocumentIcon(metadata.documentType);

    const renderProps: BaseTileRenderProps = {
      icon: Icon,
      iconColor: getIconColor(metadata.documentType),
      title: result.title,
      subtitle: metadata.department,
      preview: result.preview,
      metadata: [],
      tags: metadata.regulations?.slice(0, 3) || [],
      rightContent: (
        <div className="flex items-center gap-2">
          <DocumentTypeBadge type={metadata.documentType} />
          <ComplianceBadge status={metadata.complianceStatus} />
        </div>
      ),
    };

    // Add date metadata
    if (metadata.effectiveDate) {
      renderProps.metadata!.push({
        label: 'Effective',
        value: formatDate(metadata.effectiveDate),
        icon: Calendar,
      });
    }

    if (metadata.expirationDate) {
      renderProps.metadata!.push({
        label: 'Expires',
        value: formatDate(metadata.expirationDate),
        icon: Calendar,
      });
    }

    if (metadata.lastReviewed) {
      renderProps.metadata!.push({
        label: 'Reviewed',
        value: formatDate(metadata.lastReviewed),
        icon: Calendar,
      });
    }

    return renderProps;
  }
);

export default OperationsTile;
